import { useMemo, useState } from "react";
import { Form, Input, Modal, Select, Row, Col } from "antd";
import SimpleTableTemplate from "@/components/Templates/SimpleTableTemplate";
import { registerRisks } from "@/views/Risk/riskRegisterData";
import {
  useOpenRecordFromParam,
  useSelectableTableView,
} from "@/utils/tableHelpers";
import { getUniqueOptionsFromField } from "@/views/Risk/shared/utils";
import {
  createRiskColumns,
  type RiskTableRow,
} from "@/views/Risk/Risks/risksTableConfig";
import RisksDrawer from "@/views/Risk/Risks/RisksDrawer";

const riskRows = registerRisks;

export default function Risks() {
  const { selectedRecord, setSelectedRecord, getClickableRowProps } =
    useSelectableTableView<RiskTableRow>();
  const columns = useMemo(() => createRiskColumns(riskRows), []);

  const [openModal, setOpenModal] = useState(false);
  const [form] = Form.useForm();

  const onAddRecord = () => {
    setOpenModal(true);
  };

  const assigneesOptions = getUniqueOptionsFromField({
    data: riskRows,
    field: "assignee",
  });

  const ownersOptions = getUniqueOptionsFromField({
    data: riskRows,
    field: "owners",
  });

  const statusOptions = getUniqueOptionsFromField({
    data: riskRows,
    field: "status",
  });

  const categoryOptions = getUniqueOptionsFromField({
    data: riskRows,
    field: "category",
  });

  const onSubmit = () => {
    form.resetFields();
    setOpenModal(false);
  };

  useOpenRecordFromParam({
    paramName: "openRisk",
    rows: riskRows,
    idField: "risk_id",
    setSelectedRecord,
  });

  return (
    <div className="flex w-full min-w-0 flex-col p-4">
      <SimpleTableTemplate
        title="Risks"
        tooltipText="Review trial risks, their current status, scores, supporting signals, and mitigation plans."
        fileName="risks"
        data={riskRows}
        columns={columns}
        rowKey="risk_id"
        onRow={getClickableRowProps}
        onAddRecord={onAddRecord}
        inertDownloadButton
      />
      <RisksDrawer
        records={riskRows}
        selectedRecord={selectedRecord}
        setSelectedRecord={setSelectedRecord}
        isNewRecord={false}
      />
      <Modal
        title="Add a new entry"
        open={openModal}
        onCancel={() => setOpenModal(false)}
        okText="Create"
        cancelText="Cancel"
        okButtonProps={{ autoFocus: true, htmlType: "submit" }}
        destroyOnHidden
        modalRender={(dom) => (
          <Form
            layout="vertical"
            form={form}
            name="form_in_modal"
            initialValues={{ modifier: "public" }}
            clearOnDestroy
            onFinish={onSubmit}
          >
            {dom}
          </Form>
        )}
      >
        <Form.Item label="Risk type" name="risk">
          <Input />
        </Form.Item>
        <Form.Item label="Risk details" name="details">
          <Input.TextArea allowClear />
        </Form.Item>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label="Category" name="category">
              <Select options={categoryOptions} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Status" name="status">
              <Select options={statusOptions} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Mitigation plan" name="mitigation_plan">
          <Input.TextArea allowClear />
        </Form.Item>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label="Owners" name="owners">
              <Select options={ownersOptions} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Assignee" name="assignee">
              <Select options={assigneesOptions} />
            </Form.Item>
          </Col>
        </Row>
      </Modal>
    </div>
  );
}
